import { popupGallery } from './popupGallery';
import { hoverDynamic } from './hover';

export function randomGallery() {
  const { openModal } = popupGallery();

  const els = document.querySelectorAll(".random-gallery");
  if (els.length) {
    els.forEach((el) => {
      const breakpoints = {
        desktop: 1024, // Default (1024px and above)
        tablet: 768,   // Tablet (768px to 1023px)
        mobile: 0      // Mobile (767px and below)
      };

      // Default configuration for the gallery
      const defaultConfig = {
        minSizePercent: 50,  // Minimum size as percentage of container
        maxSizePercent: 120,  // Maximum size as percentage of container
        interval: 3000, // Time interval between adding medias (in milliseconds)
        maxMedias: 9, // Maximum number of medias to display at once
        mediasPerBatch: 1, // Number of medias to add at once
        minLeftPercent: -20, // Minimum left position as percentage (can be negative)
        maxLeftPercent: 70, // Maximum left position as percentage
        minTopPercent: -20, // Minimum top position as percentage (can be negative)
        maxTopPercent: 90, // Maximum top position as percentage
        videoRatio: 50 // Percentage chance of selecting a video (0-100)
      };

      // Create responsive configurations
      const responsiveConfig = {
        desktop: { ...defaultConfig },
        tablet: { ...defaultConfig },
        mobile: { ...defaultConfig },
        container: el,
        currentBreakpoint: 'desktop' // Default breakpoint
      };

      // Helper function to get current breakpoint based on window width
      const getCurrentBreakpoint = () => {
        const windowWidth = window.innerWidth;
        if (windowWidth >= breakpoints.desktop) {
          return 'desktop';
        } else if (windowWidth >= breakpoints.tablet) {
          return 'tablet';
        } else {
          return 'mobile';
        }
      };

      // Helper function to get config for current breakpoint
      const getActiveConfig = () => {
        return responsiveConfig[responsiveConfig.currentBreakpoint];
      };

      // Parse data attributes for a specific breakpoint
      const parseDataAttributes = (prefix = '') => {
        const config = {};
        const attrs = [
          'minSizePercent', 'maxSizePercent', 'interval', 'maxMedias', 'mediasPerBatch',
          'minLeftPercent', 'maxLeftPercent', 'minTopPercent', 'maxTopPercent', 'videoRatio'
        ];

        attrs.forEach(attr => {
          const dataAttr = prefix ? `${attr}${prefix}` : attr;
          if (el.dataset[dataAttr] !== undefined) {
            config[attr] = parseInt(el.dataset[dataAttr], 10);
          }
        });

        return config;
      };

      // Apply data attributes to responsive config
      // Default (no prefix)
      Object.assign(responsiveConfig.desktop, parseDataAttributes());
      
      // Tablet specific (1023px and below)
      Object.assign(responsiveConfig.tablet, {
        ...responsiveConfig.desktop,
        ...parseDataAttributes('Tablet')
      });
      
      // Mobile specific (767px and below)
      Object.assign(responsiveConfig.mobile, {
        ...responsiveConfig.tablet,
        ...parseDataAttributes('Mobile')
      });

      // Set initial breakpoint
      responsiveConfig.currentBreakpoint = getCurrentBreakpoint();
      
      // Extract medias from the JSON
      let medias = media_json && media_json.medias ? media_json.medias : [];
      let images = [];
      let videos = [];
      let currentImageIndex = 0;
      let currentVideoIndex = 0;
      let galleryInterval;
      
      // Separate images and videos
      function separateMediaTypes() {
        images = medias.filter(media => media.type === 'image');
        videos = medias.filter(media => media.type === 'video');
      }
      
      // Video preloading management
      const videoPreloadQueue = [];
      let isPreloading = false;
      let preloadedVideos = new Map(); // Store preloaded video URLs
      
      // Fisher-Yates shuffle algorithm to randomize the medias array
      function shuffleArray(array) {
        if (!array || !array.length) return [];
        for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
      }
      
      // Get random number between min and max (inclusive)
      function getRandomNumber(min, max) {
        return Math.floor(Math.random() * (max - min + 1)) + min;
      }
      
      // Preload videos sequentially
      function preloadNextVideo() {
        if (isPreloading || videoPreloadQueue.length === 0) return;
        
        isPreloading = true;
        const videoUrl = videoPreloadQueue.shift();
        
        // Skip if already preloaded
        if (preloadedVideos.has(videoUrl)) {
          isPreloading = false;
          preloadNextVideo();
          return;
        }
        
        // Create video element for preloading
        const video = document.createElement('video');
        video.src = videoUrl;
        video.preload = 'auto';
        video.muted = true;
        
        video.addEventListener('loadeddata', () => {
          preloadedVideos.set(videoUrl, true);
          isPreloading = false;
          
          // If gallery has started and we're waiting for videos, trigger an immediate update
          if (galleryInterval) {
            console.log('Video preloaded:', videoUrl);
            // You could optionally trigger an immediate gallery update here
            // addImageToGallery();
          }
          
          // Preload next video
          preloadNextVideo();
        });
        
        video.addEventListener('error', () => {
          console.warn('Failed to preload video:', videoUrl);
          isPreloading = false;
          // Continue with next video even on error
          preloadNextVideo();
        });
        
        // Start loading
        video.load();
      }
      
      // Initialize video preload queue
      function initializeVideoPreloading() {
        // Clear existing queue
        videoPreloadQueue.length = 0;
        
        // Add all video URLs to the queue
        medias.forEach(item => {
          if (item.type === 'video' && item.media) {
            videoPreloadQueue.push(item.media);
          }
        });
        
        // Start preloading
        preloadNextVideo();
      }
      
      // Add medias to the gallery with random size and position
      function addImageToGallery() {
        // Get active config based on current breakpoint
        const config = getActiveConfig();
        
        // Add multiple medias based on mediasPerBatch config
        for (let i = 0; i < config.mediasPerBatch; i++) {
          let selectedMedia = null;
          
          // Decide whether to show an image or video based on videoRatio
          const shouldShowVideo = Math.random() * 100 < config.videoRatio;
          
          if (shouldShowVideo && videos.length > 0) {
            // Try to find a preloaded video
            let videoAttempts = 0;
            while (videoAttempts < videos.length) {
              if (currentVideoIndex >= videos.length) {
                currentVideoIndex = 0;
              }
              
              const currentVideo = videos[currentVideoIndex];
              
              // Check if video is preloaded
              if (preloadedVideos.has(currentVideo.media)) {
                selectedMedia = currentVideo;
                currentVideoIndex++;
                break;
              }
              
              currentVideoIndex++;
              videoAttempts++;
            }
          }
          
          // If no video was selected (or we should show an image), select an image
          if (!selectedMedia && images.length > 0) {
            if (currentImageIndex >= images.length) {
              currentImageIndex = 0;
            }
            selectedMedia = images[currentImageIndex];
            currentImageIndex++;
          }
          
          // If no media could be selected, skip this batch
          if (!selectedMedia) {
            console.log('No suitable media available');
            break;
          }
          
          const currentMedia = selectedMedia;
          
          const containerWidth = responsiveConfig.container.offsetWidth;
          const containerHeight = responsiveConfig.container.offsetHeight;
          
          // Generate random size as percentage of container
          const sizePercent = getRandomNumber(config.minSizePercent, config.maxSizePercent);
          const size = Math.floor(Math.min(containerWidth, containerHeight) * (sizePercent / 100));
          
          // Generate random position
          const left = getRandomNumber(config.minLeftPercent, config.maxLeftPercent);
          const top = getRandomNumber(config.minTopPercent, config.maxTopPercent);
          
          const isVideo = currentMedia.type === 'video';
          let anchor;
          
          if (currentMedia.url) {
            // If URL is provided, create a link
            anchor = document.createElement('a');
            anchor.href = currentMedia.url;
            anchor.classList.add('db', 'position-absolute', 'gallery-media');
            hoverDynamic(anchor);
            
            // Check if URL is external
            const url = new URL(currentMedia.url, window.location.href);
            const currentHost = window.location.hostname;
            
            // Only add target="_blank" for external links
            if (url.hostname !== currentHost && url.hostname !== 'www.' + currentHost && currentHost !== 'www.' + url.hostname) {
              anchor.target = '_blank';
              anchor.rel = 'noopener noreferrer';
            }
            
            if (isVideo) {
              // Create video element
              const video = document.createElement('video');
              video.src = currentMedia.media;
              video.autoplay = true;
              video.loop = true;
              video.muted = true;
              video.playsInline = true;
              video.loading = 'lazy';
              anchor.appendChild(video);
              video.play()
            } else {
              // Create media element
              const img = document.createElement('img');
              img.src = currentMedia.media;
              img.loading = 'lazy';
              anchor.appendChild(img);
            }
          } else {
            if (isVideo) {
              anchor = document.createElement('a');
              anchor.href = currentMedia.media_big || currentMedia.media;
              anchor.classList.add('db', 'position-absolute', 'open-modal', 'gallery-media');
              anchor.style.cursor = 'pointer';
              hoverDynamic(anchor)
              
              const video = document.createElement('video');
              video.src = currentMedia.media;
              video.autoplay = true;
              video.loop = true;
              video.muted = true;
              video.playsInline = true;
              video.loading = 'lazy';
              video.style.pointerEvents = 'none'; // Prevent video controls from interfering
              
              anchor.appendChild(video);
              
              anchor.addEventListener('click', (e) => {
                e.preventDefault();
                const videoUrl = e.currentTarget.href;
                openModal(videoUrl, 'video');
              });
              
              // Ensure video plays
              video.addEventListener('loadeddata', () => {
                video.play().catch(e => console.log('Video play failed:', e));
              });
            } else {
              anchor = document.createElement('a');
              anchor.href = currentMedia.media_big || currentMedia.media;
              anchor.classList.add('db', 'position-absolute', 'open-modal', 'gallery-media');
              hoverDynamic(anchor)

              anchor.addEventListener('click', (e) => {
                e.preventDefault();
                const mediaUrl = e.currentTarget.href;
                openModal(mediaUrl, 'image');
              });
              
              const img = document.createElement('img');
              img.src = currentMedia.media;
              img.loading = 'lazy';
              anchor.appendChild(img);
            }
          }
          
          // Apply position and size styles to anchor
          anchor.style.width = `${size}px`;
          anchor.style.left = `${left}%`;
          anchor.style.top = `${top}%`;
          anchor.dataset.timestamp = Date.now(); // Add timestamp for tracking age
          
          // Add to container
          responsiveConfig.container.appendChild(anchor);
        }
        
        // Remove oldest medias if we exceed the maximum
        const allMedias = responsiveConfig.container.querySelectorAll('.gallery-media');
        if (allMedias.length > config.maxMedias) {
          // Sort medias by timestamp (oldest first)
          const sortedMedias = Array.from(allMedias).sort((a, b) => {
            return parseInt(a.dataset.timestamp) - parseInt(b.dataset.timestamp);
          });
          
          // Remove oldest medias to get back to maxMedias
          const mediasToRemove = allMedias.length - config.maxMedias;
          for (let i = 0; i < mediasToRemove; i++) {
            if (i < sortedMedias.length) {
              sortedMedias[i].remove();
            }
          }
        }
      }
      
      // Start the gallery
      function startGallery() {
        // Clear any existing interval
        if (galleryInterval) {
          clearInterval(galleryInterval);
        }
        
        // Check if medias exist
        if (!medias || medias.length === 0) {
          console.warn('No media items found for random gallery');
          return;
        }
        
        // Separate and shuffle media types
        separateMediaTypes();
        images = shuffleArray(images);
        videos = shuffleArray(videos);
        
        // Reset indices
        currentImageIndex = 0;
        currentVideoIndex = 0;
        
        // Initialize video preloading after entire page load
        if (document.readyState === 'complete') {
          // Page is already fully loaded
          initializeVideoPreloading();
        } else {
          // Wait for page to fully load
          window.addEventListener('load', () => {
            initializeVideoPreloading();
          });
        }
        
        // Check if there's at least one image or preloaded video to start with
        const hasAvailableMedia = images.length > 0 || 
          videos.some(video => preloadedVideos.has(video.media));
        
        if (hasAvailableMedia) {
          // Add first media immediately
          addImageToGallery();
        } else {
          console.log('Gallery waiting for videos to preload...');
        }
        
        // Get active config based on current breakpoint
        const config = getActiveConfig();
        
        // Set interval to add more medias
        galleryInterval = setInterval(addImageToGallery, config.interval);
      }
      
      // Reset the gallery
      function resetGallery() {
        // Clear interval
        if (galleryInterval) {
          clearInterval(galleryInterval);
          galleryInterval = null;
        }
        
        // Clear container
        while (responsiveConfig.container.firstChild) {
          responsiveConfig.container.removeChild(responsiveConfig.container.firstChild);
        }
        
        // Reset indices
        currentImageIndex = 0;
        currentVideoIndex = 0;
        
        // Clear video preloading state
        videoPreloadQueue.length = 0;
        preloadedVideos.clear();
        isPreloading = false;
      }

      // Track window width for resize detection
      let previousWindowWidth = window.innerWidth;
      
      // Handle window resize
      const handleResize = () => {
        const currentWindowWidth = window.innerWidth;
        
        // Only respond to width changes, ignore height changes
        if (currentWindowWidth !== previousWindowWidth) {
          const newBreakpoint = getCurrentBreakpoint();
          
          // Update the tracked width and breakpoint
          previousWindowWidth = currentWindowWidth;
          responsiveConfig.currentBreakpoint = newBreakpoint;
          
          // Reset and restart the gallery
          resetGallery();
          startGallery();
        }
      };
      
      // Add resize event listener
      window.addEventListener('resize', handleResize);
      
      // Start the gallery initially
      startGallery();
    })
  }
}
